import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';
import './WarEffortPurposeSelection.css';

const PURPOSE_TO_FACTION_MAPPING = {
  'Consume': ['Asrah', 'The Hollow', 'The Forsaken Pact'],
  'Eradicate': ['Exolot Dominion', 'The Corrupted Core', 'The Iron Alliance'],
  'Colonize': ['League of Shadows', 'The Conspiracy', 'The Shadow Consortium'],
  'Protect': ['Ra\'s Children', 'The Divine Syndicate', 'The Covenant'],
  'Survive': ['Remnants of Humanity', 'The Last Crusade', 'The Silent Accord']
};

const PURPOSE_IMAGES = {
  'Consume': 'asrah_ground.jpg',
  'Eradicate': 'exolot_landing.jpg',
  'Colonize': 'league_portal.jpg',
  'Protect': 'ra_seat.jpg',
  'Survive': 'remnants_headquarter.jpg'
};

const PURPOSE_DESCRIPTIONS = {
  'Consume': 'All that exists is the spread of Asrah\'s essence. It is not a choice—only the inevitable continuation of a hunger older than memory. Worlds fall, civilizations vanish, and still the mission persists without end or mercy. Earth is simply the next resistance to be dissolved. Advance toward the enemy\'s strongholds and swallow their leadership from within, piece by piece, until silence is the only thing left.',
  'Eradicate': 'Asrah was defeated once, but the victory carved too deep a wound to forget. The cost was unbearable, and the shadow of that sacrifice still haunts every step forward. Allowing Asrah to rise again is impossible; allowing him to escape justice is unforgivable. No ally, no obstacle, no innocent will change the course now. The debt must be paid in full. The corruption will end—permanently.',
  'Colonize': 'The opening of the portals revealed a world unguarded and unprepared. The Shadowverse did not request entry, nor did it require permission. Its denizens stepped forward driven by curiosity and by the quiet, unshakable belief that this place—Earth—was theirs by right. A new frontier, ripe for reshaping. Claim it, bend it, and establish dominion before anyone else can stake a claim.',
  'Protect': 'Earth remains flawed, fragile, and ungrateful, yet bound to you by ancient ties that time has failed to sever. Humanity has forgotten its benefactors, but the oath once spoken still stands. In their darkest hour—when gods are a myth and hope is a rumor—you return once more. Not for recognition. Not for praise. But because protection is the last obligation left in a world slipping toward oblivion.',
  'Survive': 'Humanity stands at the edge of extinction. Their weapons fail, their leaders fall, and Asrah advances as an unstoppable tide. Surrender is logical, but never accepted. Instead, the remnants gather in ruined shelters and burning streets, stitching together desperate plans. Every battle is hopeless. Every victory is borrowed time. Yet they fight, not for triumph, but because survival is the final rebellion the species can offer.'
};

function WarEffortPurposeSelection({ user, onPurposeSelected }) {
  const { t } = useTranslation();
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handlePurposeSelect = (purpose) => {
    setSelectedPurpose(purpose);
    setError(null);
  };
  
  const handleStartMission = async () => {
    if (!selectedPurpose) {
      setError('Please select a purpose');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/war-effort/select-purpose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          purpose: selectedPurpose
        })
      });
      
      if (response.ok) {
        const mission = await response.json();
        onPurposeSelected(mission);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start mission');
      }
    } catch (error) {
      console.error('Error starting mission:', error);
      setError('Failed to start mission');
    } finally {
      setLoading(false);
    }
  };
  
  const purposes = ['Consume', 'Eradicate', 'Colonize', 'Protect', 'Survive'];
  
  return (
    <div className="war-effort-purpose-selection">
      <h2>Select Your Purpose</h2>
      <p className="purpose-description">Choose your purpose to begin a mission. Your purpose determines which factions you can play.</p>
      
      <div className="purpose-buttons">
        {purposes.map(purpose => (
          <button
            key={purpose}
            className={`purpose-button ${selectedPurpose === purpose ? 'selected' : ''}`}
            onClick={() => handlePurposeSelect(purpose)}
            disabled={loading}
            style={{
              backgroundImage: `url(/images/${PURPOSE_IMAGES[purpose]})`
            }}
          >
            <div className="purpose-content">
              <div className="purpose-name">{purpose}</div>
              <div className="purpose-factions">
                {PURPOSE_TO_FACTION_MAPPING[purpose].join(', ')}
              </div>
            </div>
          </button>
        ))}
      </div>
      
      {selectedPurpose && (
        <div className="purpose-selected-description">
          <p className="purpose-selected-text">{PURPOSE_DESCRIPTIONS[selectedPurpose]}</p>
        </div>
      )}
      
      {error && (
        <div className="error-message">{error}</div>
      )}
      
      {selectedPurpose && (
        <button
          className="start-mission-button"
          onClick={handleStartMission}
          disabled={loading}
        >
          {loading ? 'Starting Mission...' : 'Start Mission'}
        </button>
      )}
    </div>
  );
}

export default WarEffortPurposeSelection;
